import { useState, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Table, Tag, Space, Popover, message, Input, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import moment from 'moment-timezone'
import {
  ArmAndCohortSelectorPopover,
  ConfirmModal,
  CopySoaTableModal,
  FinalizateBar,
  FinalizateButton,
  FinalizeSoaModal,
  MultiSwitch,
} from 'src/DigitalProtocolModule/components'
import {
  getDpSoaList,
  updateUnitInSoaTableList,
  getDpUnits,
  deleteSoaTableById,
  updateArmCohortInSoaTableList,
  updateSoaTableName,
  getDpCohortList,
  getDpArmList,
} from 'src/DigitalProtocolModule/utils/ajax-proxy'
import { getEmail } from 'src/utils/auth';
import { useSelector } from "react-redux";
import { fetchHistory } from "src/actions/historyTrial";
import styles from './index.module.scss';
import './index.scss';
import threeDotsIcon from 'src/assets/svgs/icon-three-dots.svg';

export default function ScheduleOfActivity() {
  const history = useHistory();
  const trial_uuid = localStorage.getItem('trial-id');

  const finalizeStatus = useSelector(fetchHistory).data?.dpFinalizeStatus;

  const isLocked = useMemo(
    () => Boolean(finalizeStatus?.soa_finalize_flag),
    [finalizeStatus]
  );

  const [unit, setUnit] = useState('Day');
  const [winUnit, setWinUnit] = useState('Day');
  const [allCohorts, setAllCohorts] = useState<
  Digital_Protocol_Arm_Types.Option[]
  >([]);
  const [optionsList, setOptionsList] = useState<
    Digital_Protocol_Arm_Types.Option[]
  >([]);
  const [tableData, setTableData] = useState<
    Digital_Protocol_SoA.LandingInfoType[]
  >([]);
  const [isShowDelConfim, setIsShowDelConfim] =
    useState<Digital_Protocol_SoA.deleteConfirmType>({
      isOpen: false,
    });
  const [isShowCreate, setIsShowCreate] = useState(false);
  const [isShowFinalize, setIsShowFinalize] = useState(false);
  const [list, setList] = useState<Digital_Protocol_Arm_Types.Cohort[]>([]);
  const [selectedCohorts, setSelectedCohorts] = useState('');

  const fetchCohortList = async () => {
    try {
      const res: Digital_Protocol_Arm_Types.Cohort[] = await getDpCohortList(
        trial_uuid
      );
      setList(res || []);
    } catch (error) {
      message.error('Failed to fetch the Cohorts');
    } finally {
    }
  }

  useEffect(() => {
    fetchCohortList();
  }, [])

  useEffect(() => {    
    if (!Array.isArray(tableData) || !Array.isArray(list)) {  
      console.error('Invalid data structure provided');  
      return;  
    }  

    const count = tableData.reduce((acc, item) => acc + (item?.cohorts?.length || 0), 0);  
    const listCount = list.length;  
    
    if (count < listCount) {  
      setSelectedCohorts(`You have mapped ${count} of ${listCount} cohorts to SOA tables. All cohorts must be mapped in order to proceed.`);  
    } else {  
      setSelectedCohorts('');  
    }  
  }, [tableData, list]);  

  const getSoaStatusStyle = (value: string) => {
    if (value?.toLowerCase().includes('progress')) {
      return styles.inProgress;
    } else if (value?.toLowerCase().includes('complete')) {
      return styles.complete;
    } else {
      return '';
    }
  }

  const fetchArmList = (armsMappedToCurrentTable: Digital_Protocol_Arm_Types.Arm[] = []) => {
    const armIdsMappedToCurrentTable = armsMappedToCurrentTable.map(
      (item) => item.arm_id
    );
    setOptionsList([]);
    getDpArmList(trial_uuid)
      .then((res: Digital_Protocol_Arm_Types.Arm[]) => {
        setOptionsList(
          res
          .filter(
            (item: Digital_Protocol_Arm_Types.Arm) =>
              armIdsMappedToCurrentTable.includes(item.arm_id) ||
              !item.arm_mapped
          )
            .map((item: Digital_Protocol_Arm_Types.Arm) => ({
              id: item.arm_id,
              name: item.arm_title,
              description: item.arm_description,
              subOptionIds: item.cohorts.map((subItem) => subItem.cohort_id),
            }))
        );
      })
      .catch((e) => {
        message.error("Failed to fetch the Arms");
      })
      .finally(() => {});
  };

  const fetchCohortListByArm = (arm_ids: string) => {
    setOptionsList([]);
    getDpArmList(trial_uuid, arm_ids)
      .then((res: Digital_Protocol_Arm_Types.Arm[]) => {
        const cohorts: Digital_Protocol_Arm_Types.Cohort[] = res.reduce(
          (accumulator, currentValue) => [
            ...accumulator,
            ...currentValue.cohorts,
          ],
          []
        );
        setOptionsList(
          cohorts.map((item) => ({
            id: item.cohort_id,
            name: item.cohort_name,
            description: item.cohort_description,
          }))
        );
      })
      .catch((e) => {
        message.error("Failed to fetch the Cohorts");
      })
      .finally(() => {});
  };

  const fetchSoAList = async () => {  
    try {  
      const res = await getDpSoaList(trial_uuid);  
      if (res?.tables) {        
        const sortedTables = res.tables.sort((a, b) => a.table_counter_id - b.table_counter_id);  
        setTableData(sortedTables);         
      } else {  
        console.error('No tables found in the response');  
      }  
    } catch (error) {  
      console.error('Error fetching SOA list:', error);  
    }  
  }

  const fetchUnits = async () => {
    try {
      const res = await getDpUnits(trial_uuid)
      if (res?.execution_status?.includes('success')) {
        setUnit(res.visit_unit)
        setWinUnit(res.visit_window_unit)
      }
    } catch (error) {}
  }

  const updateUnit = async (visit_unit: string, visit_window_unit: string) => {
    try {
      await updateUnitInSoaTableList({
        visit_unit,
        visit_window_unit,
        trial_uuid,
      })
    } catch (error) {}
  }

  const updateCohorts = async (
    data: Digital_Protocol_Arm_Cohort_Mapping.Payload
  ) => {
    let isSaved = false;
    const { arm_id, cohort_id, ...rest } = data;
    try {
      const res = await updateArmCohortInSoaTableList({
        ...rest,
        arm_id: arm_id.map((item) => item.id),
        cohort_id: cohort_id.map((item) => item.id),
      });
      if (res?.execution_status?.includes("success")) {
        fetchSoAList();
        message.success("Update successfully");
        isSaved = true;
      } else if (
        res?.error?.includes("the arm id has been assigned to other SOA table")
      ) {
        message.error("One arm cannot be linked to multiple SOA tables");
      } else {
        message.error("Update failed");
      }
    } catch (error) {
      message.error("Update failed");
    }
    return isSaved;
  };

  const onEditSoa = (record:Digital_Protocol_SoA.LandingInfoType) => {
    const user_name = getEmail()
    history.push({pathname: '/dprotocol/soa-details', state: {tableItems: record, trial_id: trial_uuid, user_name, unit, winUnit}})
  }

  const onDelSoa = async (tableId: number) => {  
    try {  
      const res = await deleteSoaTableById({ table_id: tableId });  
      if (res?.table_id) {  
        message.success(`Table deleted successfully`);  
        await fetchSoAList(); // Ensure it waits for the fetch to complete  
      } else {  
        message.error(`Failed to delete table`);  
      }  
    } catch (error) {  
      message.error(`Failed to delete table`);  
      console.error('Error deleting table:', error);  
    }  
  } 

  const totalRecords = tableData.length;   
  const tableNameChangeEvt = async (
    evt: any,
    table_id: number
  ) => {
    let isSaved = false;
    let table_name =  evt?.target?.value;
    try {
      const res = await updateSoaTableName({
        table_id,
        table_name
      })
      if (res?.update_status?.includes('success')) {
        fetchSoAList();
        message.success('Update successfully');
        isSaved = true;
      } else {
        message.error('SOA table name already exists. Please enter a unique name');
      }
    } catch (error) {
      message.error('SOA table name already exists. Please enter a unique name');
    }
    return isSaved;
  }

  const columns: ColumnsType<Digital_Protocol_SoA.LandingInfoType> = [
    {
      title: 'Name',
      dataIndex: 'table_name',
      key: 'table_name',
      render: (_, { table_name, table_id }) => (
        <Tooltip  
          title={table_name.length > 40 ? table_name : ''}  
          placement="topLeft"  
        >  
        <Input 
          className={`${styles.tableName} ${styles.inputWrapper} ${isLocked ? styles.disabledInput : ""}`}
          key={`table_${table_name}_${table_name}`} 
          defaultValue={`${table_name}`}  
          maxLength={50}
          readOnly={isLocked}
          onBlur={(e) => {
            if (isLocked) {
              return
            }
            tableNameChangeEvt(e,table_id)
          }}
        /> 
       </Tooltip>  
      ),
    },
    {
      title: "Arm",
      dataIndex: "arms",
      key: "arms",
      render: (_, { arms, cohorts, table_id }) => (
        <div className={styles.cohortWrapper}>
          {(arms || []).map((item) => (
            <Tag className={styles.cohortTag} key={item.arm_id}>
              {item.arm_title}
            </Tag>
          ))}
          {!isLocked && (
            <div onClick={() => fetchArmList(arms)}>
              <ArmAndCohortSelectorPopover
                category="Arm"
                table_id={table_id}
                allOptions={optionsList || []}
                selected={{
                  arms: (arms || []).map((item) => ({
                    id: item.arm_id,
                    name: item.arm_title,
                  })),
                  cohorts: (cohorts || []).map((item) => ({
                    id: item.cohort_id,
                    name: item.cohort_name,
                  })),
                }}
                doConfirm={updateCohorts}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Cohort",
      dataIndex: "cohorts",
      key: "cohorts",
      render: (_, { arms, cohorts, table_id }) => {
        const arms_id = (arms || []).map((item) => item.arm_id).join(",");
        return (
          <div className={styles.cohortWrapper}>
            {(cohorts || []).map((item) => {
              return (
                <Tag className={styles.cohortTag} key={item.cohort_id}>
                  {item.cohort_name}
                </Tag>
              );
            })}
            {!isLocked && (
              <div
                onClick={() => {
                  if (arms?.length > 0) {
                    fetchCohortListByArm(arms_id);
                  } else {
                    setOptionsList([]);
                  }
                }}
              >
                <ArmAndCohortSelectorPopover
                  category="Cohort"
                  table_id={table_id}
                  allOptions={optionsList || []}
                  selected={{
                    arms: (arms || []).map((item) => ({
                      id: item.arm_id,
                      name: item.arm_title,
                    })),
                    cohorts: (cohorts || []).map((item) => ({
                      id: item.cohort_id,
                      name: item.cohort_name,
                    })),
                  }}
                  errMsg={arms?.length > 0 ? "" : "Please select Arm first."}
                  doConfirm={updateCohorts}
                />
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Total Visits',
      dataIndex: 'total_visits',
      key: 'total_visits',
      width: 105,
      render: (_, { total_visits }) => (
        <>
          {total_visits && (
            <Tag className={styles.visitTag}>{total_visits}</Tag>
          )}
        </>
      ),
    },
    {
      title: 'Schedule of Activities',
      dataIndex: 'soa_status',
      key: 'soa_status',
      width: 175,
      render: (_, { soa_status }) => (
        <Tag className={`${styles.status} ${getSoaStatusStyle(soa_status)}`}>
          {soa_status}
        </Tag>
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_updated',
      key: 'last_updated',
      width: 120,
      render: (_, { last_updated }) => (
        <span className={styles.lastUpdate}>
          {moment(new Date(last_updated)).format('MMM DD YYYY')}
        </span>
      ),
    },
    {
      title: '',
      key: '',
      width: 60,
      render: (_, record) => (
        <Space size="middle">
          <Popover
            placement="bottomRight"
            trigger="click"
            className={styles.threeDots}
            content={() => (
              <div className={styles.soaActions}>
                {isLocked ? (
                  <div className={record?.cohorts === null && selectedCohorts?.length > 0 ? 'disabled-edit' : ''} onClick={() => onEditSoa(record)}>View</div>    
                ) : (
                  <>
                    <div className={record?.cohorts === null && selectedCohorts?.length > 0 ? 'disabled-edit' : ''} onClick={() => onEditSoa(record)}>Edit</div>    
                    {totalRecords > 1 &&(
                      <div
                        style={{ color: '#D1293D' }}
                        onClick={() =>
                        setIsShowDelConfim({
                            isOpen: true,
                            table_id: record.table_id,
                            table_name: record.table_name,
                          })
                        }
                      >
                        Delete
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          >
            <img src={threeDotsIcon} alt="actions" />
          </Popover>
        </Space>
      ),
    },
  ]

  const footer = () => (
    <div className={styles.tableFooter}>
      <Button
        className={styles.linkBtn}
        key="add_soa"
        type="text"
        size="small"
        onClick={() => setIsShowCreate(true)}
      >
        ADD SOA
      </Button>
    </div>
  );

  useEffect(() => {
    // get Visit Unit and Visit Window Unit
    fetchUnits()

    // get tables
    fetchSoAList()
  }, [])

  return (
    <>
      {isLocked && (
        <div className={styles.topBar}>
          <FinalizateBar category="soa" />
        </div>
      )}
      <div className={styles.header}>
        <span className={styles.title}>Schedule of Activities</span>
        <div className={styles.btns}>
          {!isLocked && <Button
            className={styles.linkBtn}
            key="finalize"
            type="link"
            onClick={() => setIsShowFinalize(true)}
            disabled={selectedCohorts?.length > 0}
          >
            Finalize
          </Button>}
          {!isLocked && <FinalizateButton category="soa" />}
        </div>
      </div>

      <div className={styles.bar}>
        <div>
          <label>Visit Unit</label>
          <MultiSwitch
            options={['Day', 'Week']}
            value={unit}
            disabled={isLocked}
            onChange={(val) => {
              if (unit !== val) {
                setUnit(val)
                updateUnit(val, winUnit)
              }
            }}
          />
        </div>
        <div>
          <label>Visit Window Unit</label>
          <MultiSwitch
            options={['Day', 'Week']}
            value={winUnit}
            disabled={isLocked}
            onChange={(val) => {
              if (winUnit !== val) {
                setWinUnit(val)
                updateUnit(unit, val)
              }
            }}
          />
        </div>
        <div className={styles.cohortCountMsg}>
        <label>{selectedCohorts}</label>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <Table
          dataSource={tableData}
          columns={columns}
          rowKey="table_counter_id" // Assuming table_counter_id is unique 
          className="dp-soa-table"
          footer={isLocked ? undefined : footer}
        />
      </div>

      <ConfirmModal
        visible={isShowDelConfim?.isOpen}
        title={`Delete Table ${isShowDelConfim?.table_name}`}
        content={
          'This action will delete the Schedule of Activity. Are you sure you want to delete?'
        }
        handleCancel={() => setIsShowDelConfim({ isOpen: false })}
        handleConfirm={() => {
          onDelSoa(isShowDelConfim?.table_id)
          setIsShowDelConfim({ isOpen: false })
        }}
      />

      <CopySoaTableModal
        visible={isShowCreate}
        tableData={tableData}
        doCancel={() => setIsShowCreate(false)}
        doConfirm={() => {
          fetchSoAList()
          setIsShowCreate(false)
        }}
      />

      {isShowFinalize && (
        <FinalizeSoaModal
          visible={isShowFinalize}
          trialUuid={trial_uuid}
          soaList={tableData}
          handleCancel={() => setIsShowFinalize(false)}
          afterConfirm={() => {
            fetchSoAList()
            setIsShowFinalize(false)
          }}
        />
      )}
    </>
  )
}
